import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DepartmentService } from '../../../../services/department.service';
import { GroupsService } from '../../../../services/groups.service';
import { ConfigOrdersService, ConfigOrder, ConfigOrderCreate, ConfigOrderUpdate } from '../../../../services/config-orders.service';
import { StatusServiceOrdersService, StatusServiceOrder } from '../../../../services/status-service-orders.service';
import { TransactionServiceOrdersService, TransactionServiceOrder, TransactionServiceOrderCreate } from '../../../../services/transaction-service-orders.service';
import {
  ServiceOrderOpeningReasonsService,
  ServiceOrderOpeningReason
} from '../../../../services/service-order-opening-reasons.service';
import {
  VehicleServiceOrderStatusChangesService,
  VehicleServiceOrderStatusChange
} from '../../../../services/vehicle-service-order-status-changes.service';
import { Department } from '../../../../models/Department';
import { Group } from '../../../../models/Group';

type TransactionDestinationMode = 'same' | 'other';

interface ServiceOrderConfigForm {
  id: number | null;
  typeOS: string;
  departmentId: number | null;
  groupId: number | null;
  description: string;

  isThirdParty: boolean;

  // Opções (não obrigatórias)
  allowSelectVehicle: boolean;
  allowAdditionalVehicle: boolean;
  allowSelectCustomer: boolean;
  allowSelectStock: boolean;
  allowAddServices: boolean;

  canClosed: boolean;
  closingStatusId: number | null;
  statusClosedServiceOrder: string | null;
  isActive: boolean;
  hasTransaction: boolean;
  closingRules: string;

  // IDs das regras (TransactionServiceOrders) vinculadas a esta configuração
  arrayTransactionServiceOrderId: number[];

  // Regras por Status para esta configuração de OS
}

@Component({
  selector: 'app-service-order-config-view-edit',
  templateUrl: './service-order-config-view-edit.component.html',
  styleUrls: ['./service-order-config-view-edit.component.css']
})
export class ServiceOrderConfigViewEditComponent implements OnInit {
  loading = false;
  saving = false;
  error: string | null = null;
  successMessage: string | null = null;
  isEditMode = false;

  availableTabs = [
    { id: 'basic', label: 'Dados da Configuração', icon: 'fas fa-file-alt', required: true, completed: false },
    { id: 'rules', label: 'Regras & Encerramento', icon: 'fas fa-clipboard-check', required: false, completed: false },
    { id: 'vehicle-status', label: 'Situação do Veiculo', icon: 'fas fa-car', required: false, completed: false }
  ];

  activeTab = 'basic';

  formData: ServiceOrderConfigForm = {
    id: null,
    typeOS: '',
    departmentId: null,
    groupId: null,
    description: '',

    isThirdParty: false,

    allowSelectVehicle: false,
    allowAdditionalVehicle: false,
    allowSelectCustomer: false,
    allowSelectStock: false,
    allowAddServices: false,

    canClosed: true,
    closingStatusId: null,
    statusClosedServiceOrder: null,
    isActive: true,
    hasTransaction: false,
    closingRules: '',
    arrayTransactionServiceOrderId: [],
  };

  departments: Department[] = [];
  groups: Group[] = [];
  loadingDepartments = false;
  loadingGroups = false;

  statuses: StatusServiceOrder[] = [];
  loadingStatuses = false;

  openingReasons: ServiceOrderOpeningReason[] = [];
  loadingOpeningReasons = false;
  newOpeningReasonName = '';
  systemOpeningReasonNames: string[] = [];
  loadingSystemReasons = false;
  selectedSystemReasonName = '';
  showCustomOpeningReasonInput = false;

  vehicleStatusChanges: VehicleServiceOrderStatusChange[] = [];
  loadingVehicleStatusChanges = false;
  newVehicleInitialStatusId: number | null = null;
  newVehicleStatusText = '';

  allConfigOrders: ConfigOrder[] = [];
  targetStatuses: StatusServiceOrder[] = [];
  loadingTargetStatuses = false;

  transactionRules: TransactionServiceOrder[] = [];
  loadingTransactionRules = false;

  destinationMode: TransactionDestinationMode = 'same';
  private statusDetailsById = new Map<number, StatusServiceOrder>();

  newRule: {
    firstStatusId: number | null;
    statusDirectionId: number | null;
    groupId: number | null;
    targetConfigOrderId: number | null;
  } = {
    firstStatusId: null,
    statusDirectionId: null,
    groupId: null,
    targetConfigOrderId: null
  };

  newExcludedStatusName = '';

  get serviceOrderNumberExample(): string | null {
    if (!this.formData.groupId) return null;
    const groupId = Number(this.formData.groupId);
    const selectedGroup = this.groups.find(g => g.id === groupId);
    const prefix = this.buildGroupPrefix(selectedGroup?.name);
    return prefix ? `${prefix}-0000` : null;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private departmentService: DepartmentService,
    private groupsService: GroupsService,
    private configOrdersService: ConfigOrdersService,
    private statusServiceOrdersService: StatusServiceOrdersService,
    private transactionServiceOrdersService: TransactionServiceOrdersService,
    private serviceOrderOpeningReasonsService: ServiceOrderOpeningReasonsService,
    private vehicleServiceOrderStatusChangesService: VehicleServiceOrderStatusChangesService
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.isEditMode = true;
      this.formData.id = Number(idParam);
    }

    this.loading = true;
    this.loadingDepartments = true;
    this.loadingGroups = true;
    this.error = null;

    const departments$ = this.departmentService.getDepartmentsPaginated(1, 100).pipe(
      catchError(err => { console.error('Erro ao carregar departamentos:', err); return of({ departments: [] as Department[] }); })
    );
    const groups$ = this.groupsService.getAllGroups().pipe(
      catchError(err => { console.error('Erro ao carregar grupos:', err); return of([] as Group[]); })
    );

    const allConfigOrders$ = this.configOrdersService.getAll().pipe(
      catchError(() => of([] as ConfigOrder[]))
    );

    if (this.formData.id) {
      this.loadingStatuses = true;
      this.loadingTransactionRules = true;

      const configOrder$ = this.configOrdersService.getById(this.formData.id).pipe(
        catchError(() => { this.error = 'Erro ao carregar dados da configuração.'; return of(null); })
      );

      forkJoin({ departments: departments$, groups: groups$, configOrder: configOrder$, allConfigOrders: allConfigOrders$ }).subscribe(results => {
        this.departments = (results.departments as any).departments || results.departments || [];
        this.loadingDepartments = false;

        this.groups = results.groups || [];
        this.loadingGroups = false;
        if (!this.newRule.groupId && this.formData.groupId) {
          this.newRule.groupId = Number(this.formData.groupId);
        }

        this.allConfigOrders = results.allConfigOrders || [];

        if (results.configOrder) {
          this.applyConfigOrderToForm(results.configOrder);
        }
        this.updateTabsCompletionStatus();

        const configId = this.formData.id!;
        const statuses$ = this.statusServiceOrdersService.getByConfigOrderId(configId).pipe(
          catchError(() => of([] as StatusServiceOrder[]))
        );
        const rules$ = this.transactionServiceOrdersService.getByConfigOrderId(configId).pipe(
          catchError(() => of([] as TransactionServiceOrder[]))
        );

        forkJoin({ statuses: statuses$, rules: rules$ }).subscribe(({ statuses, rules }) => {
          this.statuses = statuses;
          this.loadingStatuses = false;

          // Cache local de status (evita chamadas extras para descobrir Config OS do status)
          for (const s of this.statuses || []) {
            if (s?.id) this.statusDetailsById.set(Number(s.id), s);
          }

          this.transactionRules = rules;
          this.loadingTransactionRules = false;

          this.mergeLinkedTransactionRulesIntoList();
          this.prefetchStatusDetailsForTransactionRules();

          // Em alguns cenários a API devolve apenas o texto (StatusClosedServiceOrder)
          // e não devolve o ID do status de encerramento. Como o select trabalha com ID,
          // tentamos inferir o closingStatusId pelo nome assim que a lista de status estiver carregada.
          this.syncClosingStatusIdFromName();
          this.syncClosingStatusNameFromId();

          this.loadOpeningReasons();
          this.loadVehicleStatusChanges();
          this.loading = false;
          this.updateTabsCompletionStatus();

          if (this.formData.id && !this.newRule.targetConfigOrderId) {
            this.newRule.targetConfigOrderId = this.formData.id;
            this.targetStatuses = [...this.statuses];
          }

          this.syncDestinationModeWithTargetConfig();
        });
      });
    } else {
      forkJoin({ departments: departments$, groups: groups$, allConfigOrders: allConfigOrders$ }).subscribe(results => {
        this.departments = (results.departments as any).departments || results.departments || [];
        this.loadingDepartments = false;

        this.groups = results.groups || [];
        this.loadingGroups = false;

        this.allConfigOrders = results.allConfigOrders || [];

        this.loading = false;
        this.updateTabsCompletionStatus();
      });
    }
  }

  private resolveStatusNameById(statusId: number | null): string | null {
    const id = Number(statusId);
    if (!Number.isFinite(id) || id <= 0) return null;

    const cached = this.statusDetailsById.get(id);
    if (cached?.name) return cached.name;

    const local = (this.statuses || []).find(s => Number(s.id) === id);
    return (local?.name || '').trim() || null;
  }

  private normalizeForCompare(value: string | null | undefined): string {
    return (value || '')
      .toString()
      .trim()
      .normalize('NFD')
      .replace(/([\u0300-\u036f])/g, '')
      .replace(/\s+/g, ' ')
      .toUpperCase();
  }

  private syncClosingStatusIdFromName(): void {
    if (!this.formData.canClosed) return;

    // Se já temos um ID, não sobrescreve.
    const currentId = Number(this.formData.closingStatusId);
    if (Number.isFinite(currentId) && currentId > 0) return;

    const desiredName = this.normalizeForCompare(this.formData.statusClosedServiceOrder);
    if (!desiredName) return;

    const match = (this.statuses || []).find(s => this.normalizeForCompare(s?.name) === desiredName);
    if (!match?.id) return;

    this.formData.closingStatusId = Number(match.id);
  }

  private syncClosingStatusNameFromId(): void {
    if (!this.formData.canClosed) {
      this.formData.statusClosedServiceOrder = null;
      return;
    }

    const nameFromId = this.resolveStatusNameById(this.formData.closingStatusId);
    if (nameFromId) {
      this.formData.statusClosedServiceOrder = nameFromId;
      return;
    }

    // Se o backend já devolveu o texto, mantém.
    const current = (this.formData.statusClosedServiceOrder || '').trim();
    this.formData.statusClosedServiceOrder = current ? current : null;
  }

  private syncDestinationModeWithTargetConfig(): void {
    if (!this.formData.id) return;
    const isSame = Number(this.newRule.targetConfigOrderId) === Number(this.formData.id);
    this.destinationMode = isSame ? 'same' : 'other';
  }

  onDestinationModeChange(mode: TransactionDestinationMode): void {
    this.destinationMode = mode;

    if (mode === 'same') {
      this.newRule.targetConfigOrderId = this.formData.id;
      this.targetStatuses = [...(this.statuses || [])];

      // Para mesma OS, o grupo é o da própria configuração (não precisa selecionar na UI)
      if (this.formData.groupId) {
        this.newRule.groupId = Number(this.formData.groupId);
      }
    } else {
      // força usuário a escolher a Config OS destino (para carregar status)
      this.newRule.targetConfigOrderId = null;
      this.targetStatuses = [];

      // Ao ir para "outra OS", mantém o grupo atual como sugestão, mas permite troca
      if (!this.newRule.groupId && this.formData.groupId) {
        this.newRule.groupId = Number(this.formData.groupId);
      }
    }

    this.newRule.statusDirectionId = null;
  }

  onCanClosedChange(value: boolean): void {
    this.formData.canClosed = value;
    if (!value) {
      this.formData.closingStatusId = null;
      this.formData.statusClosedServiceOrder = null;
    }
  }

  onClosingStatusIdChange(statusId: number | null): void {
    this.formData.closingStatusId = statusId;
    if (!this.formData.canClosed) {
      this.formData.statusClosedServiceOrder = null;
      return;
    }

    const resolvedName = this.resolveStatusNameById(statusId);
    this.formData.statusClosedServiceOrder = resolvedName;
  }

  onGroupIdChange(): void {
    this.updateTabsCompletionStatus();
    if (!this.newRule.groupId && this.formData.groupId) {
      this.newRule.groupId = Number(this.formData.groupId);
    }
    this.loadTransactionRules();
  }

  onTargetConfigOrderChange(): void {
    this.newRule.statusDirectionId = null;
    this.targetStatuses = [];

    const targetConfigId = this.newRule.targetConfigOrderId;
    if (!targetConfigId) return;

    // mantém o modo sincronizado com a seleção
    if (this.formData.id && Number(targetConfigId) === Number(this.formData.id)) {
      this.destinationMode = 'same';
    } else {
      this.destinationMode = 'other';
    }

    this.loadingTargetStatuses = true;
    this.statusServiceOrdersService.getByConfigOrderId(Number(targetConfigId)).subscribe({
      next: (items) => {
        this.targetStatuses = items || [];

        for (const s of this.targetStatuses || []) {
          if (s?.id) this.statusDetailsById.set(Number(s.id), s);
        }

        this.loadingTargetStatuses = false;
      },
      error: () => {
        this.targetStatuses = [];
        this.loadingTargetStatuses = false;
      }
    });
  }

  get hasPersistedConfigOrder(): boolean {
    return !!this.formData.id;
  }

  private loadConfigOrder(id: number): void {
    if (!id) return;
    this.loading = true;
    this.error = null;

    this.configOrdersService.getById(id).subscribe({
      next: (configOrder) => {
        this.applyConfigOrderToForm(configOrder);
        this.loadTransactionRules();
        this.loading = false;
        this.updateTabsCompletionStatus();
      },
      error: () => {
        this.loading = false;
        this.error = 'Erro ao carregar dados da configuração.';
      }
    });
  }

  loadDepartments(): void {
    this.loadingDepartments = true;
    this.departmentService.getDepartmentsPaginated(1, 100).subscribe({
      next: (response) => {
        this.departments = response.departments || [];
        this.loadingDepartments = false;
      },
      error: (err) => {
        console.error('Erro ao carregar departamentos:', err);
        this.loadingDepartments = false;
      }
    });
  }

  loadGroups(): void {
    this.loadingGroups = true;
    this.groupsService.getAllGroups().subscribe({
      next: (groups) => {
        this.groups = groups || [];
        if (!this.newRule.groupId && this.formData.groupId) {
          this.newRule.groupId = Number(this.formData.groupId);
        }
        this.loadingGroups = false;
      },
      error: (err) => {
        console.error('Erro ao carregar grupos:', err);
        this.loadingGroups = false;
      }
    });
  }

  private loadStatuses(): void {
    this.loadingStatuses = true;
    const configOrderId = this.formData.id;
    if (!configOrderId) {
      this.statuses = [];
      this.loadingStatuses = false;
      return;
    }

    this.statusServiceOrdersService.getByConfigOrderId(configOrderId).subscribe({
      next: (items) => {
        this.statuses = items || [];
        this.loadingStatuses = false;

        for (const s of this.statuses || []) {
          if (s?.id) this.statusDetailsById.set(Number(s.id), s);
        }

        if (this.newRule.targetConfigOrderId && Number(this.newRule.targetConfigOrderId) === this.formData.id) {
          this.targetStatuses = [...this.statuses];
        }

        this.syncClosingStatusNameFromId();

        this.prefetchStatusDetailsForTransactionRules();
      },
      error: () => {
        this.loadingStatuses = false;
      }
    });
  }

  private loadOpeningReasons(): void {
    const configOrderId = this.formData.id;
    if (!configOrderId) {
      this.openingReasons = [];
      return;
    }

    this.loadingOpeningReasons = true;
    this.serviceOrderOpeningReasonsService.getByConfigOrderId(Number(configOrderId)).subscribe({
      next: (items) => {
        this.openingReasons = items || [];
        this.loadingOpeningReasons = false;
      },
      error: () => {
        this.openingReasons = [];
        this.loadingOpeningReasons = false;
      }
    });

    this.loadSystemOpeningReasons();
  }

  private loadSystemOpeningReasons(): void {
    this.loadingSystemReasons = true;
    this.serviceOrderOpeningReasonsService.getAll().subscribe({
      next: (items) => {
        const names = (items || [])
          .map(r => (r.statusName || '').trim())
          .filter(Boolean);
        this.systemOpeningReasonNames = [...new Set(names)].sort();
        this.loadingSystemReasons = false;
      },
      error: () => {
        this.systemOpeningReasonNames = [];
        this.loadingSystemReasons = false;
      }
    });
  }

  get availableSystemReasonNames(): string[] {
    const alreadyAdded = new Set((this.openingReasons || []).map(r => (r.statusName || '').trim()));
    return (this.systemOpeningReasonNames || []).filter(name => !alreadyAdded.has(name));
  }

  toggleCustomOpeningReasonInput(): void {
    this.showCustomOpeningReasonInput = !this.showCustomOpeningReasonInput;
    if (!this.showCustomOpeningReasonInput) {
      this.newOpeningReasonName = '';
    } else {
      this.selectedSystemReasonName = '';
    }
  }

  addOpeningReasonFromSelect(): void {
    const name = (this.selectedSystemReasonName || '').trim();
    if (!name) return;

    if (!this.formData.id) {
      this.error = 'Salve a configuração antes de adicionar status de abertura.';
      return;
    }

    this.serviceOrderOpeningReasonsService.create({ statusName: name, configOrderId: this.formData.id }).subscribe({
      next: () => {
        this.selectedSystemReasonName = '';
        this.loadOpeningReasons();
      },
      error: () => {
        this.error = 'Erro ao criar status de abertura.';
      }
    });
  }

  private loadVehicleStatusChanges(): void {
    const configOrderId = this.formData.id;
    if (!configOrderId) {
      this.vehicleStatusChanges = [];
      return;
    }

    this.loadingVehicleStatusChanges = true;
    this.vehicleServiceOrderStatusChangesService.getByConfigOrderId(Number(configOrderId)).subscribe({
      next: (items) => {
        this.vehicleStatusChanges = items || [];
        this.loadingVehicleStatusChanges = false;
      },
      error: () => {
        this.vehicleStatusChanges = [];
        this.loadingVehicleStatusChanges = false;
      }
    });
  }

  addVehicleStatusChange(): void {
    const statusId = this.newVehicleInitialStatusId;
    const newVehicleStatus = (this.newVehicleStatusText || '').trim();
    if (!statusId || !newVehicleStatus) return;

    if (!this.formData.id) {
      this.error = 'Salve a configuração antes de adicionar status de veículo.';
      return;
    }

    const initialStatusName = this.resolveStatusNameById(statusId);
    if (!initialStatusName) {
      this.error = 'Selecione um status válido da OS.';
      return;
    }

    this.vehicleServiceOrderStatusChangesService
      .create({
        initialStatusName,
        newStatusVeichile: newVehicleStatus,
        configOrderId: this.formData.id
      })
      .subscribe({
        next: () => {
          this.newVehicleInitialStatusId = null;
          this.newVehicleStatusText = '';
          this.loadVehicleStatusChanges();
        },
        error: () => {
          this.error = 'Erro ao salvar status de veículo.';
        }
      });
  }

  removeVehicleStatusChangeById(id: number): void {
    if (!id) return;

    this.vehicleServiceOrderStatusChangesService.delete(id).subscribe({
      next: () => this.loadVehicleStatusChanges(),
      error: () => {
        this.error = 'Erro ao remover status de veículo.';
      }
    });
  }

  addOpeningReason(): void {
    const name = (this.newOpeningReasonName || '').trim();
    if (!name) return;

    if (!this.formData.id) {
      this.error = 'Salve a configuração antes de adicionar status de abertura.';
      return;
    }

    this.serviceOrderOpeningReasonsService.create({ statusName: name, configOrderId: this.formData.id }).subscribe({
      next: () => {
        this.newOpeningReasonName = '';
        this.showCustomOpeningReasonInput = false;
        this.loadOpeningReasons();
      },
      error: () => {
        this.error = 'Erro ao criar status de abertura.';
      }
    });
  }

  removeOpeningReasonById(id: number): void {
    if (!id) return;

    this.serviceOrderOpeningReasonsService.delete(id).subscribe({
      next: () => this.loadOpeningReasons(),
      error: () => {
        this.error = 'Erro ao remover status de abertura.';
      }
    });
  }

  private prefetchStatusDetailsForTransactionRules(): void {
    const ids = Array.from(
      new Set(
        (this.transactionRules || [])
          .flatMap(r => [r.firstStatusId, r.statusDirectionId])
          .filter((x): x is number => Number.isFinite(Number(x)) && Number(x) > 0)
          .map(x => Number(x))
      )
    );

    const missing = ids.filter(id => !this.statusDetailsById.has(id));
    if (!missing.length) return;

    forkJoin(
      missing.map(id =>
        this.statusServiceOrdersService.getById(id).pipe(
          catchError(() => of(null))
        )
      )
    ).subscribe(results => {
      for (const item of (results || [])) {
        if (item?.id) this.statusDetailsById.set(Number(item.id), item);
      }
    });
  }

  getStatusConfigOrderId(statusId: number | null | undefined): number | null {
    const id = Number(statusId);
    if (!Number.isFinite(id) || id <= 0) return null;
    const cached = this.statusDetailsById.get(id);
    return cached?.configOrderId ?? null;
  }

  getRuleDestinationConfigOrderId(rule: TransactionServiceOrder): number | null {
    return this.getStatusConfigOrderId(rule?.statusDirectionId ?? null);
  }

  getRuleDestinationConfigOrderType(rule: TransactionServiceOrder): string {
    const configId = this.getRuleDestinationConfigOrderId(rule);
    if (!configId) return '';
    return this.getConfigOrderTypeName(configId);
  }

  getRuleStaysInSameConfig(rule: TransactionServiceOrder): boolean | null {
    const currentConfigId = this.formData.id;
    const destinationConfigId = this.getRuleDestinationConfigOrderId(rule);
    if (!currentConfigId || !destinationConfigId) return null;
    return Number(destinationConfigId) === Number(currentConfigId);
  }

  getRuleSummary(rule: TransactionServiceOrder): string {
    const from = rule.firstStatusName || this.getStatusName(rule.firstStatusId);
    const to = rule.statusDirectionName || this.getStatusName(rule.statusDirectionId);
    const group = this.getGroupName(rule.groupId);
    const destinationConfig = this.getRuleDestinationConfigOrderType(rule);

    const stays = this.getRuleStaysInSameConfig(rule);
    const staysLabel = stays === null ? '—' : (stays ? 'Sim' : 'Não');

    const destinationLabel = destinationConfig ? `${destinationConfig} / ${to}` : to;
    return `Quando estiver em "${from}", tramita para "${destinationLabel}" (Permanece na mesma OS? ${staysLabel}) | Grupo: ${group}`;
  }

  private loadTransactionRules(): void {
    this.loadingTransactionRules = true;
    const configOrderId = this.formData.id;
    const request$ = configOrderId
      ? this.transactionServiceOrdersService.getByConfigOrderId(Number(configOrderId))
      : (this.formData.groupId
        ? this.transactionServiceOrdersService.getByGroup(Number(this.formData.groupId))
        : this.transactionServiceOrdersService.getAll());

    request$.subscribe({
      next: (items) => {
        this.transactionRules = items || [];
        this.mergeLinkedTransactionRulesIntoList();
        this.prefetchStatusDetailsForTransactionRules();
        this.loadingTransactionRules = false;
      },
      error: () => {
        this.loadingTransactionRules = false;
      }
    });
  }

  private mergeLinkedTransactionRulesIntoList(): void {
    const linkedIds = (this.formData.arrayTransactionServiceOrderId || [])
      .map(x => Number(x))
      .filter(x => Number.isFinite(x) && x > 0);

    if (!linkedIds.length) return;

    const existingIds = new Set((this.transactionRules || []).map(r => r.id));
    const missingIds = linkedIds.filter(id => !existingIds.has(id));
    if (!missingIds.length) return;

    forkJoin(
      missingIds.map(id =>
        this.transactionServiceOrdersService.getById(id).pipe(
          catchError(() => of(null))
        )
      )
    ).subscribe(rules => {
      const valid = (rules || []).filter(Boolean) as TransactionServiceOrder[];
      if (!valid.length) return;

      const merged = [...(this.transactionRules || []), ...valid];
      const uniqueById = new Map<number, TransactionServiceOrder>();
      for (const rule of merged) {
        uniqueById.set(rule.id, rule);
      }
      this.transactionRules = Array.from(uniqueById.values());
    });
  }

  getStatusName(statusId: number | null): string {
    const id = Number(statusId);
    if (!Number.isFinite(id) || id <= 0) return '';

    const cached = this.statusDetailsById.get(id);
    if (cached?.name) return cached.name;

    const local = (this.statuses || []).find(s => Number(s.id) === id);
    return local?.name || `#${id}`;
  }

  getDepartmentName(departmentId: number | null): string {
    if (!departmentId) return '';
    return this.departments.find(d => d.id === departmentId)?.name || `#${departmentId}`;
  }

  getGroupName(groupId: number | null): string {
    if (!groupId) return '';
    return this.groups.find(g => g.id === groupId)?.name || `#${groupId}`;
  }

  getConfigOrderTypeName(configOrderId: number | null): string {
    if (!configOrderId) return '';
    return this.allConfigOrders.find(c => c.id === configOrderId)?.typeOS || `#${configOrderId}`;
  }

  isRuleLinked(ruleId: number): boolean {
    return (this.formData.arrayTransactionServiceOrderId || []).includes(ruleId);
  }

  toggleRuleLinked(ruleId: number, checked: boolean): void {
    const current = new Set(this.formData.arrayTransactionServiceOrderId || []);
    if (checked) current.add(ruleId);
    else current.delete(ruleId);
    this.formData.arrayTransactionServiceOrderId = Array.from(current);

    // Se vinculou alguma regra, marca que existe transação
    if (this.formData.arrayTransactionServiceOrderId.length > 0) {
      this.formData.hasTransaction = true;
    }
  }

  addStatus(): void {
    const name = (this.newExcludedStatusName || '').trim();
    if (!name) return;

    if (!this.formData.id) {
      this.error = 'Salve a configuração antes de adicionar status.';
      return;
    }

    this.statusServiceOrdersService.create({ name, configOrderId: this.formData.id }).subscribe({
      next: (created) => {
        this.newExcludedStatusName = '';

        if (created?.id) {
          const exists = (this.statuses || []).some(s => s.id === created.id);
          if (!exists) {
            this.statuses = [...(this.statuses || []), created];
          }
        }

        this.loadStatuses();
      },
      error: () => {
        this.error = 'Erro ao criar status de OS.';
      }
    });
  }

  removeStatusById(statusId: number): void {
    if (!statusId) return;

    this.statusServiceOrdersService.delete(statusId).subscribe({
      next: () => {
        this.loadStatuses();
      },
      error: () => {
        this.error = 'Erro ao remover status de OS.';
      }
    });
  }

  addTransactionRule(): void {
    const firstStatusId = this.newRule.firstStatusId;
    const statusDirectionId = this.newRule.statusDirectionId;
    const groupId = this.newRule.groupId ?? this.formData.groupId;
    const configOrderId = this.formData.id;

    if (!firstStatusId || !statusDirectionId || !groupId) {
      this.error = 'Preencha Status inicial, Status destino e Grupo para criar a regra.';
      return;
    }

    if (!configOrderId) {
      this.error = 'Salve a configuração antes de adicionar regras de transação.';
      return;
    }

    const payload: TransactionServiceOrderCreate = {
      firstStatusId: Number(firstStatusId),
      statusDirectionId: Number(statusDirectionId),
      groupId: Number(groupId),
      configOrderId: Number(configOrderId)
    };

    this.transactionServiceOrdersService.create(payload).subscribe({
      next: (created) => {
        this.error = null;
        if (created?.id) {
          this.toggleRuleLinked(created.id, true);
        }
        this.newRule.firstStatusId = null;
        this.newRule.statusDirectionId = null;
        this.newRule.groupId = this.formData.groupId ? Number(this.formData.groupId) : null;
        this.newRule.targetConfigOrderId = this.formData.id || null;
        this.targetStatuses = [...this.statuses];
        this.destinationMode = 'same';
        this.loadTransactionRules();
      },
      error: () => {
        this.error = 'Erro ao criar regra de transação. Recarregando lista...';
        this.loadTransactionRules();
      }
    });
  }

  removeTransactionRule(ruleId: number): void {
    if (!ruleId) return;

    this.transactionServiceOrdersService.delete(ruleId).subscribe({
      next: () => {
        this.formData.arrayTransactionServiceOrderId = (this.formData.arrayTransactionServiceOrderId || []).filter(x => x !== ruleId);
        this.loadTransactionRules();
      },
      error: () => {
        this.error = 'Erro ao remover regra de transação.';
      }
    });
  }


  selectTab(tabId: string): void {
    if ((tabId === 'rules' || tabId === 'vehicle-status') && !this.hasPersistedConfigOrder) {
      this.error = 'Salve os dados básicos da configuração antes de acessar esta aba.';
      this.activeTab = 'basic';
      return;
    }
    this.error = null;
    this.activeTab = tabId;
  }

  getCurrentTabIndex(): number {
    return this.availableTabs.findIndex(tab => tab.id === this.activeTab);
  }

  isFirstTab(): boolean {
    return this.getCurrentTabIndex() === 0;
  }

  isLastTab(): boolean {
    return this.getCurrentTabIndex() === this.availableTabs.length - 1;
  }

  goToNextTab(): void {
    if (!this.isTabValid(this.activeTab)) {
      alert('Por favor, preencha os campos obrigatórios antes de avançar.');
      return;
    }

    const currentIndex = this.getCurrentTabIndex();
    if (currentIndex < this.availableTabs.length - 1) {
      const nextTabId = this.availableTabs[currentIndex + 1].id;

      // Fluxo em etapas: só pode ir para Regras após salvar (POST) e obter Id
      if (this.activeTab === 'basic' && nextTabId === 'rules' && !this.hasPersistedConfigOrder) {
        this.createConfigOrderAndAdvance();
        return;
      }

      this.availableTabs[currentIndex].completed = true;
      this.activeTab = nextTabId;
      this.updateTabsCompletionStatus();
    }
  }

  goToPreviousTab(): void {
    const currentIndex = this.getCurrentTabIndex();
    if (currentIndex > 0) {
      this.activeTab = this.availableTabs[currentIndex - 1].id;
    }
  }

  isTabValid(tabId: string): boolean {
    if (tabId === 'basic') {
      return !!this.formData.typeOS && !!this.formData.departmentId && !!this.formData.groupId;
    }
    if (tabId === 'rules') {
      return this.hasPersistedConfigOrder;
    }
    if (tabId === 'vehicle-status') {
      return this.hasPersistedConfigOrder;
    }
    return true;
  }

  updateTabsCompletionStatus(): void {
    this.availableTabs = this.availableTabs.map(tab => ({
      ...tab,
      completed: this.isTabValid(tab.id)
    }));
  }

  onSave(): void {
    if (!this.isTabValid('basic')) {
      this.activeTab = 'basic';
      this.updateTabsCompletionStatus();
      alert('Por favor, preencha os campos obrigatórios antes de salvar.');
      return;
    }

    if (this.formData.canClosed && !this.formData.closingStatusId) {
      this.error = 'Selecione o status que permitirá o encerramento.';
      return;
    }

    if (!this.formData.id) {
      // Proteção: por regra de fluxo, não deveria ser possível salvar a aba final sem Id.
      this.error = 'Salve os dados básicos antes de finalizar.';
      this.activeTab = 'basic';
      return;
    }

    this.saving = true;
    this.error = null;
    this.successMessage = null;

    const payload = this.buildUpdatePayloadFromForm();

    this.configOrdersService.update(this.formData.id, payload).subscribe({
      next: (updated) => {
        this.applyConfigOrderToForm(updated);
        this.loadTransactionRules();
        this.loadStatuses();
        this.loadOpeningReasons();
        this.loadVehicleStatusChanges();
        this.saving = false;
        this.successMessage = 'Configuração de OS atualizada com sucesso!';
      },
      error: () => {
        this.saving = false;
        this.error = 'Erro ao atualizar configuração de OS.';
      }
    });
  }

  private createConfigOrderAndAdvance(): void {
    if (this.saving) return;
    if (!this.isTabValid('basic')) return;

    this.saving = true;
    this.error = null;
    this.successMessage = null;

    const payload = this.buildCreatePayloadFromForm();

    this.configOrdersService.create(payload).subscribe({
      next: (created) => {
        this.applyConfigOrderToForm(created);
        this.isEditMode = true;
        if (created && !this.allConfigOrders.some(c => c.id === created.id)) {
          this.allConfigOrders = [...this.allConfigOrders, created];
        }
        this.newRule.targetConfigOrderId = created.id ?? this.formData.id ?? null;
        this.destinationMode = 'same';
        this.loadStatuses();
        this.loadTransactionRules();
        this.loadOpeningReasons();
        this.loadVehicleStatusChanges();
        this.saving = false;
        this.successMessage = 'Configuração de OS criada com sucesso!';
        this.activeTab = 'rules';
        this.updateTabsCompletionStatus();
      },
      error: () => {
        this.saving = false;
        this.error = 'Erro ao salvar dados básicos da configuração.';
      }
    });
  }

  private buildCreatePayloadFromForm(): ConfigOrderCreate {
    const closingStatusId = this.formData.canClosed && this.formData.closingStatusId
      ? Number(this.formData.closingStatusId)
      : null;

    const statusClosedServiceOrder = this.formData.canClosed
      ? (this.resolveStatusNameById(closingStatusId) ?? (this.formData.statusClosedServiceOrder || null))
      : null;

    return {
      typeOS: this.formData.typeOS,
      departmentId: Number(this.formData.departmentId),
      groupId: Number(this.formData.groupId),
      statusClosedServiceOrder,
      transaction: !!this.formData.hasTransaction,
      isThirdParty: !!this.formData.isThirdParty,
      canVehicle: !!this.formData.allowSelectVehicle,
      canAditionalVehicle: !!this.formData.allowAdditionalVehicle,
      canClient: !!this.formData.allowSelectCustomer,
      canStock: !!this.formData.allowSelectStock,
      canServices: !!this.formData.allowAddServices,
      active: !!this.formData.isActive,
      canClose: !!this.formData.canClosed,
      ...(closingStatusId ? { closingStatusId } : {}),
      arrayTransactionServiceOrderId: (this.formData.arrayTransactionServiceOrderId || []).map(x => Number(x))
    };
  }

  private buildUpdatePayloadFromForm(): ConfigOrderUpdate {
    const closingStatusId = this.formData.canClosed
      ? (this.formData.closingStatusId ? Number(this.formData.closingStatusId) : null)
      : null;

    const statusClosedServiceOrder = this.formData.canClosed
      ? (this.resolveStatusNameById(closingStatusId) ?? (this.formData.statusClosedServiceOrder || null))
      : null;

    return {
      typeOS: this.formData.typeOS,
      departmentId: Number(this.formData.departmentId),
      groupId: Number(this.formData.groupId),
      statusClosedServiceOrder,
      transaction: !!this.formData.hasTransaction,
      isThirdParty: !!this.formData.isThirdParty,
      canVehicle: !!this.formData.allowSelectVehicle,
      canAditionalVehicle: !!this.formData.allowAdditionalVehicle,
      canClient: !!this.formData.allowSelectCustomer,
      canStock: !!this.formData.allowSelectStock,
      canServices: !!this.formData.allowAddServices,
      active: !!this.formData.isActive,
      canClose: !!this.formData.canClosed,
      closingStatusId,
      arrayTransactionServiceOrderId: (this.formData.arrayTransactionServiceOrderId || []).map(x => Number(x))
    };
  }

  private applyConfigOrderToForm(configOrder: ConfigOrder): void {
    const arrayTransactionServiceOrderId = Array.isArray(configOrder.arrayTransactionServiceOrderId)
      ? configOrder.arrayTransactionServiceOrderId
      : [];

    const closingStatusId = Number(configOrder?.closingStatusId);

    this.formData = {
      ...this.formData,
      id: configOrder.id || this.formData.id,
      typeOS: configOrder.typeOS || this.formData.typeOS,
      departmentId: configOrder.departmentId ?? this.formData.departmentId,
      groupId: configOrder.groupId ?? this.formData.groupId,
      isThirdParty: !!(configOrder.isThirdParty ?? this.formData.isThirdParty),
      allowSelectVehicle: !!(configOrder.canVehicle ?? this.formData.allowSelectVehicle),
      allowAdditionalVehicle: !!(configOrder.canAditionalVehicle ?? this.formData.allowAdditionalVehicle),
      allowSelectCustomer: !!(configOrder.canClient ?? this.formData.allowSelectCustomer),
      allowSelectStock: !!(configOrder.canStock ?? this.formData.allowSelectStock),
      allowAddServices: !!(configOrder.canServices ?? this.formData.allowAddServices),
      isActive: (configOrder.active ?? this.formData.isActive) as boolean,
      canClosed: (configOrder.canClose ?? this.formData.canClosed) as boolean,
      closingStatusId: Number.isFinite(closingStatusId) && closingStatusId > 0 ? closingStatusId : (this.formData.closingStatusId ?? null),
      statusClosedServiceOrder: configOrder.statusClosedServiceOrder ?? this.formData.statusClosedServiceOrder ?? null,
      hasTransaction: (configOrder.transaction ?? this.formData.hasTransaction) as boolean,
      arrayTransactionServiceOrderId: (arrayTransactionServiceOrderId || []).map(x => Number(x))
    };

    if (!this.newRule.groupId && this.formData.groupId) {
      this.newRule.groupId = Number(this.formData.groupId);
    }
  }

  onCancel(): void {
    this.router.navigate(['/service-orders/config']);
  }

  private buildGroupPrefix(groupName: string | undefined): string | null {
    const normalized = (groupName || '')
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();

    const lettersOnly = normalized.replace(/[^A-Z0-9]+/g, '');
    if (!lettersOnly) return null;
    return lettersOnly.slice(0, 2);
  }
}
